import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import { COLORS } from "../../../constants";

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

function formatISODateToBR(iso: string): string {
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

function fileDateSuffix(): string {
  return `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}-${String(new Date().getDate()).padStart(2, "0")}`;
}

export type RelatorioBrindeFiltroResumo = { label: string; value: string };

export interface RelatorioBrindeExportInput {
  periodoInicioISO: string;
  periodoFimISO: string;
  filtros: RelatorioBrindeFiltroResumo[];
  linhas: Array<{
    lojaNome: string;
    tamanhoNome: string;
    saborNome: string;
    motivo: string;
    categoria: string;
    quantidade: number;
  }>;
  totalQuantidadeFracao: number;
  geradoEmLabel: string;
}

function formatQuantidade(valor: number): string {
  return valor.toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

type JsPdfWithAutoTable = jsPDF & {
  lastAutoTable?: { finalY: number };
};

export function exportRelatorioBrindeXlsx(
  input: RelatorioBrindeExportInput
): void {
  const pIni = formatISODateToBR(input.periodoInicioISO);
  const pFim = formatISODateToBR(input.periodoFimISO);
  const rows: (string | number)[][] = [
    ["Dom Belloni"],
    ["Relatório — Pizzas brinde"],
    [],
    ["Período", `${pIni} a ${pFim}`],
    ...input.filtros.map((f) => [f.label, f.value]),
    [],
    ["Totais por loja, tamanho, sabor, categoria e motivo"],
    ["Loja", "Tamanho", "Sabor", "Categoria", "Motivo", "Quantidade"],
    ...input.linhas.map((r) => [
      r.lojaNome,
      r.tamanhoNome,
      r.saborNome,
      r.categoria,
      r.motivo,
      formatQuantidade(r.quantidade),
    ]),
    [],
    [
      "Total (soma frac.)",
      "",
      "",
      "",
      "",
      formatQuantidade(input.totalQuantidadeFracao),
    ],
    [],
    ["Gerado em", input.geradoEmLabel],
  ];
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws["!cols"] = [
    { wch: 22 },
    { wch: 18 },
    { wch: 28 },
    { wch: 14 },
    { wch: 36 },
    { wch: 14 },
  ];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Relatório");
  XLSX.writeFile(wb, `relatorio-pizzas-brinde-${fileDateSuffix()}.xlsx`);
}

export function exportRelatorioBrindePdf(
  input: RelatorioBrindeExportInput
): void {
  const primary = hexToRgb(COLORS.PRIMARY);
  const primaryDark = hexToRgb(COLORS.PRIMARY_DARK);
  const pIni = formatISODateToBR(input.periodoInicioISO);
  const pFim = formatISODateToBR(input.periodoFimISO);

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 14;

  doc.setFillColor(primary[0], primary[1], primary[2]);
  doc.rect(0, 0, pageW, 28, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("Pizzas brinde", margin, 12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("Dom Belloni · Fila integração", margin, 19);

  autoTable(doc, {
    startY: 32,
    body: [
      ["Período", `${pIni}  até  ${pFim}`],
      ...input.filtros.map((f) => [f.label, f.value]),
    ],
    theme: "plain",
    styles: {
      fontSize: 9,
      textColor: [42, 42, 42],
      cellPadding: 2.5,
    },
    columnStyles: {
      0: {
        fontStyle: "bold",
        cellWidth: 36,
      },
      1: { cellWidth: pageW - margin * 2 - 36 },
    },
    margin: { left: margin, right: margin },
  });

  const docExt = doc as JsPdfWithAutoTable;
  const afterMeta = (docExt.lastAutoTable?.finalY ?? 40) + 5;

  autoTable(doc, {
    startY: afterMeta,
    head: [["Loja", "Tamanho", "Sabor", "Categoria", "Motivo", "Qtde."]],
    body: input.linhas.map((r) => [
      r.lojaNome,
      r.tamanhoNome,
      r.saborNome,
      r.categoria,
      r.motivo,
      formatQuantidade(r.quantidade),
    ]),
    foot: [
      [
        "Total (soma frac.)",
        "",
        "",
        "",
        "",
        formatQuantidade(input.totalQuantidadeFracao),
      ],
    ],
    theme: "striped",
    headStyles: {
      fillColor: primary,
      textColor: 255,
      fontStyle: "bold",
      halign: "left",
      valign: "middle",
    },
    footStyles: {
      fillColor: primaryDark,
      textColor: 255,
      fontStyle: "bold",
    },
    columnStyles: {
      0: { cellWidth: 22, halign: "left" },
      1: { cellWidth: 20, halign: "left" },
      2: { cellWidth: 26, halign: "left" },
      3: { cellWidth: 18, halign: "left" },
      4: {
        cellWidth: pageW - margin * 2 - 22 - 20 - 26 - 18 - 16,
        halign: "left",
      },
      5: { cellWidth: 16, halign: "right" },
    },
    bodyStyles: {
      textColor: [38, 38, 38],
      lineColor: [230, 230, 230],
    },
    alternateRowStyles: {
      fillColor: [255, 252, 251],
    },
    margin: { left: margin, right: margin, bottom: 16 },
    styles: {
      fontSize: 9,
      cellPadding: { top: 3, bottom: 3, left: 2.5, right: 2.5 },
      lineWidth: 0.08,
    },
  });

  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFontSize(7.5);
    doc.setTextColor(115, 115, 115);
    doc.setFont("helvetica", "normal");
    doc.text(
      `${input.geradoEmLabel}  ·  Folha ${i} de ${pages}`,
      margin,
      pageH - 6
    );
  }

  doc.save(`relatorio-pizzas-brinde-${fileDateSuffix()}.pdf`);
}
